import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Button } from '@/components/ui/button';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { Icon } from '@/components/common/Icon';
import { OwnerSection } from '@/components/practitioner/PatientForm/OwnerSection';
import {
  createOwnerDraft,
  isOwnerDraftValid,
  toOwnerChoice,
  type OwnerDraft,
  type OwnerMode,
} from '@/components/practitioner/PatientForm/ownerDraft';
import { useAssignPatientOwnerMutation } from '@/hooks/usePatientMutations';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

const ASSIGN_OWNER_MODES: OwnerMode[] = ['new', 'existing'];

interface AddOwnerDialogProps {
  patientId: string;
  patientName: string;
  onClose: () => void;
}

export function AddOwnerDialog({ patientId, patientName, onClose }: AddOwnerDialogProps) {
  const { t } = useTranslation(['patients', 'common']);
  const assignMutation = useAssignPatientOwnerMutation();
  const [draft, setDraft] = useState<OwnerDraft>(createOwnerDraft('new'));
  const [showErrors, setShowErrors] = useState(false);

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    const owner = toOwnerChoice(draft);
    if (isOwnerDraftValid(draft) === false || owner.kind === 'later') {
      setShowErrors(true);
      return;
    }
    assignMutation.mutate(
      { patientId, owner },
      {
        onSuccess: function ownerAdded() {
          showSuccessNotification(t('patients:owner.added'));
          onClose();
        },
        onError: function ownerNotAdded(saveError: unknown) {
          const backendMessage = resolveApiErrorMessage(saveError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t('patients:form.saveError')
          );
        },
      }
    );
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <form
        onSubmit={submit}
        className="w-full max-w-lg max-h-[90vh] flex flex-col bg-zoowhite border border-zooborder rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zooborder">
          <h2 className="font-bold text-zooindigo">
            {t('patients:owner.addTitle', { name: patientName })}
          </h2>
          <button type="button" aria-label={t('common:actions.close')} onClick={onClose}>
            <Icon name="X" className="text-zoomuted" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <OwnerSection
            draft={draft}
            modes={ASSIGN_OWNER_MODES}
            showErrors={showErrors}
            onChange={setDraft}
          />
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zooborder bg-zoooffwhite">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" isLoading={assignMutation.isPending}>
            {t('patients:owner.addAction')}
          </Button>
        </div>
      </form>
    </ModalOverlay>
  );
}
